import type {
  BuildingDefinition,
  BuildingType,
  ResourceNode,
  UnitDefinition,
  UnitType,
  WorldPoint,
} from './types'

export const GRID_WIDTH = 19
export const GRID_HEIGHT = 11
export const CELL_SIZE = 1.45

export const BUILDING_DEFS: Record<BuildingType, BuildingDefinition> = {
  heartforge: {
    type: 'heartforge',
    label: 'Heartforge',
    description: 'The living command core. Keep yours beating and break theirs.',
    cost: {},
    maxHp: 550,
    powerUse: 0,
    powerOutput: 12,
  },
  veinway: {
    type: 'veinway',
    label: 'Veinway',
    description: 'A cheap conductive link that carries power, matter, and intent.',
    cost: { fossil: 2 },
    maxHp: 72,
    powerUse: 0,
    powerOutput: 0,
  },
  fossilRig: {
    type: 'fossilRig',
    label: 'Fossil Rig',
    description: 'Coaxes dense fossil matter from a matching deposit.',
    cost: { fossil: 10 },
    maxHp: 130,
    powerUse: 2,
    powerOutput: 0,
    productionTime: 1.4,
  },
  amberTap: {
    type: 'amberTap',
    label: 'Amber Tap',
    description: 'Draws luminous amber from a matching deposit.',
    cost: { fossil: 10 },
    maxHp: 130,
    powerUse: 2,
    powerOutput: 0,
    productionTime: 1.4,
  },
  alloyForge: {
    type: 'alloyForge',
    label: 'Alloy Forge',
    description: 'Compresses two fossil into one combat-grade alloy.',
    cost: { fossil: 14 },
    maxHp: 160,
    powerUse: 3,
    powerOutput: 0,
    productionTime: 3,
  },
  geneVat: {
    type: 'geneVat',
    label: 'Gene Vat',
    description: 'Refines two amber into one programmable genome.',
    cost: { fossil: 14 },
    maxHp: 160,
    powerUse: 3,
    powerOutput: 0,
    productionTime: 3,
  },
  dynamo: {
    type: 'dynamo',
    label: 'Pulse Dynamo',
    description: 'Adds eight power to its connected Heartforge network.',
    cost: { fossil: 12, alloy: 2 },
    maxHp: 145,
    powerUse: 0,
    powerOutput: 8,
  },
  incubator: {
    type: 'incubator',
    label: 'War Incubator',
    description: 'Grows autonomous warbeasts and releases them by lane doctrine.',
    cost: { fossil: 18, alloy: 4, genome: 2 },
    maxHp: 205,
    powerUse: 4,
    powerOutput: 0,
  },
  spineTurret: {
    type: 'spineTurret',
    label: 'Spine Turret',
    description: 'Snaps alloy spines at nearby enemies. Every tenth shot costs alloy.',
    cost: { fossil: 14, alloy: 3 },
    maxHp: 170,
    powerUse: 3,
    powerOutput: 0,
  },
}

export const UNIT_DEFS: Record<UnitType, UnitDefinition> = {
  clawpack: {
    type: 'clawpack',
    label: 'Clawpack',
    description: 'Fast, fragile hunters that shred exposed logistics.',
    cost: { alloy: 2, genome: 2 },
    buildTime: 4,
    maxHp: 38,
    speed: 3.15,
    damage: 7,
    attackCooldown: 0.55,
    attackRange: 1.05,
    aggroRange: 3.4,
    buildingMultiplier: 0.72,
    splashRadius: 0,
  },
  shellback: {
    type: 'shellback',
    label: 'Shellback',
    description: 'A durable line-holder whose impacts rattle nearby enemies.',
    cost: { alloy: 4, genome: 3 },
    buildTime: 7,
    maxHp: 118,
    speed: 1.85,
    damage: 13,
    attackCooldown: 1.15,
    attackRange: 1.2,
    aggroRange: 3.6,
    buildingMultiplier: 1,
    splashRadius: 1.35,
  },
  ramhorn: {
    type: 'ramhorn',
    label: 'Ramhorn',
    description: 'A slow siege beast bred to collapse structures and cores.',
    cost: { alloy: 7, genome: 5 },
    buildTime: 11,
    maxHp: 185,
    speed: 1.25,
    damage: 27,
    attackCooldown: 1.7,
    attackRange: 1.35,
    aggroRange: 3.2,
    buildingMultiplier: 2.75,
    splashRadius: 0,
  },
}

export const RESOURCE_NODES: readonly ResourceNode[] = Object.freeze([
  { id: 'fossil-west-near', gx: 4, gz: 2, kind: 'fossil', richness: 1 },
  { id: 'amber-west-near', gx: 4, gz: 8, kind: 'amber', richness: 1 },
  { id: 'fossil-west-deep', gx: 7, gz: 1, kind: 'fossil', richness: 1.15 },
  { id: 'amber-west-deep', gx: 7, gz: 9, kind: 'amber', richness: 1.15 },
  { id: 'fossil-east-near', gx: 14, gz: 2, kind: 'fossil', richness: 1 },
  { id: 'amber-east-near', gx: 14, gz: 8, kind: 'amber', richness: 1 },
  { id: 'fossil-east-deep', gx: 11, gz: 1, kind: 'fossil', richness: 1.15 },
  { id: 'amber-east-deep', gx: 11, gz: 9, kind: 'amber', richness: 1.15 },
  { id: 'fossil-center-bonus', gx: 9, gz: 3, kind: 'fossil', richness: 1.4 },
  { id: 'amber-center-bonus', gx: 9, gz: 7, kind: 'amber', richness: 1.4 },
])

export function gridToWorld(gx: number, gz: number): WorldPoint {
  return {
    x: (gx - (GRID_WIDTH - 1) / 2) * CELL_SIZE,
    z: (gz - (GRID_HEIGHT - 1) / 2) * CELL_SIZE,
  }
}
